import { useEffect, useState } from "react";
import { toast } from "sonner";
import { fetchSettings, adminUpdateSettings } from "@/lib/api";
import { useSettings } from "@/context/SettingsContext";
import { Loader2, Save, Bell, BellOff } from "lucide-react";

const TIMING = ["start_hour", "end_hour", "frequency", "weekday"];

// Describes every editable built-in message. `fields` are the editable keys.
const CATALOG = [
  { group: "Daily reminders (in-app bell)", items: [
    { key: "reminder_festival", label: "Festival reminder", note: "Sent when a festival is within 3 days. Use {name}.", fields: ["en", "hi", ...TIMING], toggle: true },
    { key: "reminder_sandhya", label: "Sandhya Vandana reminder", note: "Fires within the time window (IST).", fields: ["en", "hi", ...TIMING], toggle: true },
    { key: "reminder_daily_pooja", label: "Daily Pooja reminder", note: "Best in the morning — set a window & frequency.", fields: ["en", "hi", ...TIMING], toggle: true },
    { key: "reminder_japa", label: "Japa reminder", fields: ["en", "hi", ...TIMING], toggle: true },
  ]},
  { group: "Community notifications (bell)", note: "These fire on real activity. Timing/quiet-hours suppress the alert if it falls outside the window.", items: [
    { key: "notif_reply", label: "New reply/answer", note: "Placeholders: {who}, {what}", fields: ["en", "hi", ...TIMING] },
    { key: "notif_upvote", label: "Upvote", note: "Placeholders: {who}, {more}, {what}", fields: ["en", "hi", ...TIMING] },
    { key: "notif_best", label: "Best answer", note: "Placeholder: {who}", fields: ["en", "hi", ...TIMING] },
  ]},
  { group: "Prompts, gates & nudges", items: [
    { key: "gate_content", label: "Free-reading gate", note: "Shown after the free limit for signed-out readers.", fields: ["free_limit", "title_en", "title_hi", "body_en", "body_hi"], toggle: true },
    { key: "gate_bookmark", label: "Bookmark sign-in prompt", fields: ["title_en", "title_hi", "body_en", "body_hi"], toggle: true },
    { key: "nudge_engagement", label: "Engagement nudge (signed-out)", fields: ["en", "hi", "cta_en", "cta_hi", ...TIMING], toggle: true },
    { key: "popup_panchang", label: "Panchangam Premium popup", note: "Toggle whether the waitlist popup appears.", fields: [...TIMING], toggle: true },
  ]},
];

const FIELD_LABEL = {
  en: "English", hi: "हिन्दी (Hindi)", title_en: "Title (English)", title_hi: "Title (Hindi)",
  body_en: "Body (English)", body_hi: "Body (Hindi)", cta_en: "Button (English)", cta_hi: "Button (Hindi)",
  free_limit: "Free entries before gate", cutoff_hour: "Cutoff hour (0–23, IST)",
  start_hour: "Earliest hour (0–23, IST)", end_hour: "Latest hour (1–24, IST)",
  frequency: "How often", weekday: "Day (when weekly)",
};
const FREQ_OPTIONS = [
  { v: "daily", label: "Every day" },
  { v: "weekdays", label: "Weekdays (Mon–Fri)" },
  { v: "weekly", label: "Weekly (on a chosen day)" },
];
const WEEKDAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
const isNum = (f) => ["free_limit", "cutoff_hour", "start_hour", "end_hour", "weekday"].includes(f);
const isSelect = (f) => f === "frequency" || f === "weekday";
const isLong = (f) => f.startsWith("body");
const DEFAULTS = { start_hour: 6, end_hour: 21, frequency: "daily", weekday: 0 };

export default function AdminSystemNotifications() {
  const { reload } = useSettings();
  const [vals, setVals] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => { fetchSettings().then((d) => setVals(d.system_messages || {})).catch(() => setVals({})); }, []);

  const setField = (key, field, v) => setVals((p) => ({ ...p, [key]: { ...(p[key] || {}), [field]: v } }));
  const setQuiet = (k, v) => setVals((p) => ({ ...p, quiet_hours: { ...(p.quiet_hours || {}), [k]: v } }));

  const save = async () => {
    setSaving(true);
    try {
      // coerce numeric fields
      const out = JSON.parse(JSON.stringify(vals));
      CATALOG.forEach((g) => g.items.forEach((it) => it.fields.forEach((f) => {
        if (isNum(f) && out[it.key] && out[it.key][f] !== undefined && out[it.key][f] !== "") out[it.key][f] = Number(out[it.key][f]);
      })));
      if (out.quiet_hours) {
        out.quiet_hours.start_hour = Number(out.quiet_hours.start_hour ?? 22);
        out.quiet_hours.end_hour = Number(out.quiet_hours.end_hour ?? 6);
      }
      await adminUpdateSettings({ system_messages: out });
      await reload();
      toast.success("System messages saved");
    } catch (err) { toast.error("Could not save", { description: err.response?.data?.detail || err.message }); }
    finally { setSaving(false); }
  };

  if (!vals) return <div className="flex items-center gap-2 py-16 text-ink-3"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>;

  return (
    <div data-testid="admin-system-notifications">
      <div className="mb-6 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="inline-flex items-center gap-2 font-serif-latin text-3xl text-ink"><Bell className="h-6 w-6 text-saffron" /> System Notifications</h1>
          <p className="mt-1 max-w-2xl text-sm text-ink-3">Turn built-in reminders, community alerts, prompts and nudges on/off and rewrite their wording (English & Hindi).</p>
        </div>
        <button onClick={save} disabled={saving} data-testid="sysmsg-save" className="inline-flex items-center gap-1.5 bg-[var(--saffron)] px-5 py-2 text-sm font-medium text-white transition-colors hover:bg-[var(--saffron-dark)] disabled:opacity-60">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save all
        </button>
      </div>

      <div className="space-y-10">
        {(() => {
          const q = vals.quiet_hours || {};
          const qon = q.enabled === true;
          return (
            <div data-testid="sysmsg-quiet-hours" className="border border-line bg-surface p-4">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <p className="font-serif-latin text-lg text-ink">Quiet hours (all notifications)</p>
                  <p className="text-xs text-ink-3">When on, every reminder, community alert and nudge is suppressed inside this IST window. Windows may wrap past midnight (e.g. 22 → 6).</p>
                </div>
                <button data-testid="sysmsg-quiet-toggle" onClick={() => setQuiet("enabled", !qon)}
                  className={`inline-flex items-center gap-1.5 border px-3 py-1.5 text-xs font-medium transition-colors ${qon ? "border-emerald-600 text-emerald-700" : "border-line text-ink-3"}`}>
                  {qon ? <Bell className="h-3.5 w-3.5" /> : <BellOff className="h-3.5 w-3.5" />} {qon ? "On" : "Off"}
                </button>
              </div>
              <div className={`mt-3 grid gap-3 sm:grid-cols-2 ${qon ? "" : "opacity-50"}`}>
                <label className="block">
                  <span className="mb-1 block text-xs uppercase tracking-wider text-ink-3">Quiet from (hour 0–23, IST)</span>
                  <input data-testid="sysmsg-quiet-start" type="number" min={0} max={23} disabled={!qon} placeholder="22"
                    value={q.start_hour ?? ""} onChange={(e) => setQuiet("start_hour", e.target.value)}
                    className="w-full border border-line bg-parchment px-3 py-2 text-sm text-ink focus:border-saffron focus:outline-none disabled:opacity-50" />
                </label>
                <label className="block">
                  <span className="mb-1 block text-xs uppercase tracking-wider text-ink-3">Quiet until (hour 0–23, IST)</span>
                  <input data-testid="sysmsg-quiet-end" type="number" min={0} max={23} disabled={!qon} placeholder="6"
                    value={q.end_hour ?? ""} onChange={(e) => setQuiet("end_hour", e.target.value)}
                    className="w-full border border-line bg-parchment px-3 py-2 text-sm text-ink focus:border-saffron focus:outline-none disabled:opacity-50" />
                </label>
              </div>
            </div>
          );
        })()}
        {CATALOG.map((grp) => (
          <div key={grp.group}>
            <h2 className="mb-1 font-serif-latin text-xl text-ink">{grp.group}</h2>
            {grp.note && <p className="mb-3 max-w-2xl text-xs text-ink-3">{grp.note}</p>}
            <div className="space-y-4">
              {grp.items.map((it) => {
                const v = vals[it.key] || {};
                const on = v.enabled !== false;
                return (
                  <div key={it.key} data-testid={`sysmsg-${it.key}`} className={`border border-line bg-surface p-4 ${on ? "" : "opacity-60"}`}>
                    <div className="flex items-center justify-between gap-3">
                      <div>
                        <p className="font-serif-latin text-lg text-ink">{it.label}</p>
                        {it.note && <p className="text-xs text-ink-3">{it.note}</p>}
                      </div>
                      {it.toggle && (
                        <button data-testid={`sysmsg-toggle-${it.key}`} onClick={() => setField(it.key, "enabled", !on)}
                          className={`inline-flex items-center gap-1.5 border px-3 py-1.5 text-xs font-medium transition-colors ${on ? "border-emerald-600 text-emerald-700" : "border-line text-ink-3"}`}>
                          {on ? <Bell className="h-3.5 w-3.5" /> : <BellOff className="h-3.5 w-3.5" />} {on ? "On" : "Off"}
                        </button>
                      )}
                    </div>
                    {it.fields.length > 0 && (
                      <div className="mt-3 grid gap-3 sm:grid-cols-2">
                        {it.fields.map((f) => (
                          <label key={f} className={isLong(f) ? "sm:col-span-2 block" : "block"}>
                            <span className="mb-1 block text-xs uppercase tracking-wider text-ink-3">{FIELD_LABEL[f] || f}</span>
                            {isSelect(f) ? (
                              <select data-testid={`sysmsg-${it.key}-${f}`}
                                value={v[f] ?? (f === "weekday" ? DEFAULTS.weekday : DEFAULTS.frequency)}
                                onChange={(e) => setField(it.key, f, f === "weekday" ? Number(e.target.value) : e.target.value)}
                                disabled={f === "weekday" && (v.frequency ?? DEFAULTS.frequency) !== "weekly"}
                                className="w-full border border-line bg-parchment px-3 py-2 text-sm text-ink focus:border-saffron focus:outline-none disabled:opacity-50">
                                {(f === "frequency" ? FREQ_OPTIONS.map((o) => ({ v: o.v, label: o.label })) : WEEKDAYS.map((d, i) => ({ v: i, label: d }))).map((o) => (
                                  <option key={o.v} value={o.v}>{o.label}</option>
                                ))}
                              </select>
                            ) : isLong(f) ? (
                              <textarea data-testid={`sysmsg-${it.key}-${f}`} rows={2} value={v[f] ?? ""} onChange={(e) => setField(it.key, f, e.target.value)} className="w-full border border-line bg-parchment px-3 py-2 text-sm text-ink focus:border-saffron focus:outline-none" />
                            ) : (
                              <input data-testid={`sysmsg-${it.key}-${f}`} type={isNum(f) ? "number" : "text"}
                                placeholder={isNum(f) && DEFAULTS[f] !== undefined ? String(DEFAULTS[f]) : ""}
                                value={v[f] ?? ""} onChange={(e) => setField(it.key, f, e.target.value)} className="w-full border border-line bg-parchment px-3 py-2 text-sm text-ink focus:border-saffron focus:outline-none" />
                            )}
                          </label>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
